import { useMemo, useState, useCallback, useEffect } from 'react';
import { orderBy, limit, doc, getDoc, setDoc, onSnapshot, serverTimestamp, type QueryConstraint } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import {
  Activity,
  AlertTriangle,
  Ban,
  Bug,
  Check,
  Copy,
  Download,
  Filter,
  Info,
  Pause,
  Play,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { useCollection } from '../../hooks/useFirestore';
import styles from './MonitorLogsPage.module.css';

// ─── Tipos ──────────────────────────────────────────────────
type Nivel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL';

interface ObsEvent {
  id: string;
  timestamp?: { toDate?: () => Date; seconds?: number } | null;
  nivel: Nivel;
  categoria: string;
  codigo: string;
  mensaje?: string;
  componente?: string | null;
  traceId?: string | null;
  consultaId?: string | null;
  salaId?: string | null;
  actor?: { uid?: string | null; rol?: string | null; nombre?: string | null } | null;
  resultado?: string | null;
  duracionMs?: number | null;
  detalle?: Record<string, unknown> | null;
  error?: { tipo?: string | null; mensaje?: string | null; stack?: string | null } | null;
  origen?: string | null;
  entorno?: string | null;
}

const NIVELES: { key: Nivel; label: string; icon: typeof Info; cls: string }[] = [
  { key: 'DEBUG', label: 'Debug', icon: Bug, cls: styles.debug },
  { key: 'INFO', label: 'Info', icon: Info, cls: styles.info },
  { key: 'WARN', label: 'Warn', icon: AlertTriangle, cls: styles.warn },
  { key: 'ERROR', label: 'Error', icon: Ban, cls: styles.error },
  { key: 'FATAL', label: 'Fatal', icon: Ban, cls: styles.fatal },
];

const CATEGORIAS = ['CITA', 'LLAMADA', 'TRANSCRIPCION', 'IA', 'API', 'CONSULTA', 'AUTH', 'SISTEMA', 'NOTIFICACION'];

// Opciones del nivel mínimo que el backend PERSISTE. El nivel más bajo (DEBUG)
// equivale a "guardar todo". Cada opción guarda ese nivel y los superiores.
const NIVEL_MIN_OPCIONES: { value: Nivel; label: string }[] = [
  { value: 'DEBUG', label: 'Todos (incl. Debug)' },
  { value: 'INFO', label: 'Info y superiores' },
  { value: 'WARN', label: 'Advertencias y superiores' },
  { value: 'ERROR', label: 'Errores y superiores' },
  { value: 'FATAL', label: 'Sólo críticos' },
];

function fmtHora(ts: ObsEvent['timestamp']): string {
  const d = ts?.toDate?.() ?? (ts?.seconds ? new Date(ts.seconds * 1000) : null);
  if (!d) return '—';
  return d.toLocaleString('es-PA', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export default function MonitorLogsPage() {
  const [nivelesSel, setNivelesSel] = useState<Set<Nivel>>(new Set());
  const [categoria, setCategoria] = useState<string>('');
  const [texto, setTexto] = useState('');
  const [trazaId, setTrazaId] = useState<string>('');
  const [pausado, setPausado] = useState(false);
  const [selected, setSelected] = useState<ObsEvent | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [showPurga, setShowPurga] = useState(false);
  const [purgaDias, setPurgaDias] = useState(30);
  const [purgaErrores, setPurgaErrores] = useState(false);
  const [purgando, setPurgando] = useState(false);
  const [purgaMsg, setPurgaMsg] = useState<string | null>(null);

  // Tiempo casi real: onSnapshot con orderBy+limit. Los filtros se aplican en
  // memoria sobre los últimos N para evitar índices compuestos.
  const constraints = useMemo<QueryConstraint[]>(
    () => [orderBy('timestamp', 'desc'), limit(300)],
    [],
  );
  const { data: eventosRaw, loading, error } = useCollection<ObsEvent>('obs_events', constraints);

  // Mapa uid → nombre de los usuarios del portal (todos viven en `medicos`),
  // para mostrar QUIÉN estaba conectado cuando ocurrió el evento. Los eventos
  // de sistema (uid null/'system') muestran el nombre del proceso (componente).
  const medicosConstraints = useMemo(() => [], []);
  const { data: medicosRaw } = useCollection<{ id: string; nombreCompleto?: string; email?: string }>(
    'medicos',
    medicosConstraints,
  );
  const nombresPorUid = useMemo(() => {
    const m = new Map<string, string>();
    (medicosRaw ?? []).forEach((u) => {
      m.set(u.id, u.nombreCompleto || u.email || u.id);
    });
    return m;
  }, [medicosRaw]);

  /** Nombre visible del autor: usuario conectado o proceso del sistema. */
  const nombreActor = useCallback((e: ObsEvent): string => {
    const uid = e.actor?.uid;
    if (!uid || uid === 'system') {
      return e.componente ? `Sistema · ${e.componente}` : 'Sistema';
    }
    return e.actor?.nombre || nombresPorUid.get(uid) || `${uid.slice(0, 8)}…`;
  }, [nombresPorUid]);

  // Nivel mínimo que se PERSISTE en Firestore (configurable en caliente). Lo lee
  // el logger backend desde configuracion/observabilidad.nivelMinimo.
  const [nivelMin, setNivelMin] = useState<Nivel>('INFO');

  useEffect(() => {
    getDoc(doc(db, 'configuracion', 'observabilidad'))
      .then((s) => { const v = s.data()?.nivelMinimo as Nivel | undefined; if (v) setNivelMin(v); })
      .catch(() => { /* usar default */ });
  }, []);
  const cambiarNivelMin = useCallback((n: Nivel) => {
    setNivelMin(n);
    void setDoc(
      doc(db, 'configuracion', 'observabilidad'),
      { nivelMinimo: n, actualizadoEn: serverTimestamp() },
      { merge: true },
    );
  }, []);

  // Copiar el evento completo (JSON) para compartir con desarrollo.
  const copiarEvento = useCallback(() => {
    if (!selected) return;
    const txt = JSON.stringify(selected, null, 2);
    navigator.clipboard?.writeText(txt)
      .then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 1500); })
      .catch(() => { /* noop */ });
  }, [selected]);

  // Cerrar el detalle con ESC.
  useEffect(() => {
    if (!selected) return;
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  // Exportar los eventos VISIBLES (con los filtros aplicados) a Excel.
  // CSV con BOM UTF-8 y separador ';' — Excel (config regional en

  // Congelar la vista cuando está en pausa.
  const [frozen, setFrozen] = useState<ObsEvent[] | null>(null);
  const eventos = useMemo(
    () => (pausado ? (frozen ?? eventosRaw ?? []) : (eventosRaw ?? [])),
    [pausado, frozen, eventosRaw],
  );

  const togglePausa = useCallback(() => {
    setPausado((p) => {
      if (!p) setFrozen(eventosRaw ?? []);
      else setFrozen(null);
      return !p;
    });
  }, [eventosRaw]);

  const toggleNivel = (n: Nivel) =>
    setNivelesSel((prev) => {
      const s = new Set(prev);
      if (s.has(n)) s.delete(n); else s.add(n);
      return s;
    });

  // Categoría normalizada: los eventos sin categoría se tratan como "Sin
  // clasificar" para que se muestren y se puedan filtrar (issue #2).
  const catDe = (e: ObsEvent) => (e.categoria?.trim() || 'SIN_CLASIFICAR');

  const filtrados = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return eventos.filter((e) => {
      if (nivelesSel.size > 0 && !nivelesSel.has(e.nivel)) return false;
      if (categoria && catDe(e) !== categoria) return false;
      if (trazaId && e.consultaId !== trazaId && e.traceId !== trazaId) return false;
      if (t) {
        const hay = `${e.codigo} ${e.mensaje ?? ''} ${e.componente ?? ''} ${e.consultaId ?? ''} ${e.actor?.uid ?? ''}`.toLowerCase();
        if (!hay.includes(t)) return false;
      }
      return true;
    });
  }, [eventos, nivelesSel, categoria, trazaId, texto]);

  /* ── Servidor de contingencia ─────────────────────────────────────────────
   * El uso del respaldo queda en los eventos (LIVEKIT_CONTINGENCIA_ACTIVADA y
   * LIVEKIT_SALA_EN_CONTINGENCIA), pero encontrarlo exige saber qué buscar.
   * Estando activo, el servicio corre sobre un proveedor distinto del habitual
   * y eso tiene que verse al entrar, no después de filtrar: se muestra arriba,
   * con desde cuándo y cuántas consultas se abrieron allá. */
  const [contingencia, setContingencia] = useState<
    { activa: boolean; desde?: Date; principal: string; respaldo: string } | null
  >(null);
  useEffect(() => onSnapshot(doc(db, 'config', 'livekit'), (snap) => {
    const d = snap.data() as {
      contingenciaActiva?: boolean; contingenciaCloud?: boolean;
      proveedor?: string; contingenciaCambiadaEn?: { toDate?: () => Date };
    } | undefined;
    // Cuál es el principal ya no se puede dar por supuesto: se lee. El nombre
    // viejo `contingenciaCloud` se sigue aceptando por si el backend aún no
    // está desplegado con el nuevo.
    const principal = d?.proveedor === 'cloud' ? 'LiveKit Cloud' : 'el servidor propio';
    const respaldo = d?.proveedor === 'cloud' ? 'el servidor propio' : 'LiveKit Cloud';
    setContingencia({
      activa: d?.contingenciaActiva === true || d?.contingenciaCloud === true,
      desde: d?.contingenciaCambiadaEn?.toDate?.(),
      principal, respaldo,
    });
  }), []);

  // Consultas abiertas en el respaldo dentro de la ventana cargada de eventos.
  const salasEnContingencia = useMemo(
    () => new Set(eventos.filter((e) => e.codigo === 'LIVEKIT_SALA_EN_CONTINGENCIA')
      .map((e) => e.consultaId ?? '')).size,
    [eventos],
  );

  const resumen = useMemo(() => {
    const r: Record<string, number> = { DEBUG: 0, INFO: 0, WARN: 0, ERROR: 0, FATAL: 0 };
    for (const e of eventos) r[e.nivel] = (r[e.nivel] ?? 0) + 1;
    return r;
  }, [eventos]);

  const nivelCls = (n: Nivel) => NIVELES.find((x) => x.key === n)?.cls ?? styles.info;

  // Exportar los eventos FILTRADOS a Excel (SpreadsheetML — Excel lo abre nativo,
  // sin dependencias). Incluye toda la metadata + error + detalle.
  const exportarExcel = useCallback(() => {
    const escX = (v: unknown) =>
      String(v ?? '')
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '')
        .replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
    const cols = [
      'Hora', 'Nivel', 'Categoría', 'Código', 'Componente', 'Resultado',
      'Duración (ms)', 'Consulta', 'Sala', 'Traza', 'Actor', 'Rol',
      'Mensaje', 'Error (tipo)', 'Error (mensaje)', 'Detalle',
    ];
    const filas = filtrados.map((e) => [
      fmtHora(e.timestamp), e.nivel, e.categoria, e.codigo, e.componente ?? '', e.resultado ?? '',
      e.duracionMs ?? '', e.consultaId ?? '', e.salaId ?? '', e.traceId ?? '',
      nombreActor(e), e.actor?.rol ?? '',
      e.mensaje ?? '', e.error?.tipo ?? '', e.error?.mensaje ?? '',
      e.detalle && Object.keys(e.detalle).length ? JSON.stringify(e.detalle) : '',
    ]);
    const filaXml = (celdas: unknown[]) =>
      `<Row>${celdas.map((c, i) => {
        const num = i === 6 && c !== '' && Number.isFinite(Number(c));
        return `<Cell><Data ss:Type="${num ? 'Number' : 'String'}">${escX(c)}</Data></Cell>`;
      }).join('')}</Row>`;
    const xml =
      `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<?mso-application progid="Excel.Sheet"?>\n` +
      `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">` +
      `<Worksheet ss:Name="Eventos"><Table>` +
      filaXml(cols) + filas.map(filaXml).join('') +
      `</Table></Worksheet></Workbook>`;
    const blob = new Blob(['﻿', xml], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fecha = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.href = url; a.download = `eventos_${fecha}.xls`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }, [filtrados, nombreActor]);

  // Purgar eventos viejos (backend admin-only; el servidor recorta el rango).
  const purgar = useCallback(async () => {
    setPurgando(true);
    setPurgaMsg(null);
    try {
      const fn = httpsCallable(functions, 'portal_purgarEventosObs');
      const res = await fn({ dias: purgaDias, incluirErrores: purgaErrores });
      const d = res.data as { borrados: number; conservados: number; dias: number };
      setPurgaMsg(`Se borraron ${d.borrados} evento(s) de más de ${d.dias} días.` +
        (d.conservados ? ` Se conservaron ${d.conservados} de error/críticos.` : ''));
    } catch (err) {
      setPurgaMsg('No se pudo purgar: ' + (err instanceof Error ? err.message : 'error'));
    } finally {
      setPurgando(false);
    }
  }, [purgaDias, purgaErrores]);

  return (
    <MainLayout pageTitle="Monitor de eventos" breadcrumb="Administración > Monitor">
      <div className={styles.wrap}>
        {contingencia?.activa && (
          <div className={styles.avisoContingencia}>
            <AlertTriangle size={16} aria-hidden="true" />
            <span>
              <strong>Servidor de contingencia activo.</strong> {contingencia.principal} no
              responde y las teleconsultas nuevas se están abriendo en {contingencia.respaldo}
              {contingencia.desde && ` desde las ${contingencia.desde.toLocaleTimeString('es-PA', { hour: '2-digit', minute: '2-digit' })}`}.
              {salasEnContingencia > 0 &&
                ` ${salasEnContingencia} consulta(s) atendida(s) allí en los eventos cargados.`}
              {' '}Volverá solo a {contingencia.principal} en cuanto responda.
            </span>
          </div>
        )}

        {/* Resumen por nivel */}
        <div className={styles.kpis}>
          {NIVELES.map((n) => (
            <button
              key={n.key}
              className={`${styles.kpi} ${n.cls} ${nivelesSel.has(n.key) ? styles.kpiActive : ''}`}
              onClick={() => toggleNivel(n.key)}
              title={`Filtrar por ${n.label}`}
            >
              <n.icon size={14} />
              <span className={styles.kpiVal}>{resumen[n.key] ?? 0}</span>
              <span className={styles.kpiLbl}>{n.label}</span>
            </button>
          ))}
          <div className={styles.spacer} />
          <button className={styles.pauseBtn} onClick={exportarExcel} disabled={filtrados.length === 0} title="Descargar los eventos filtrados en Excel">
            <Download size={14} /> Excel
          </button>
          <button className={styles.pauseBtn} onClick={() => { setPurgaMsg(null); setShowPurga(true); }} title="Borrar eventos antiguos">
            <Trash2 size={14} /> Purgar
          </button>
          <button className={styles.pauseBtn} onClick={togglePausa}>
            {pausado ? <Play size={14} /> : <Pause size={14} />}
            {pausado ? 'Reanudar' : 'Pausar'}
          </button>
        </div>

        {/* Filtros */}
        <div className={styles.filtros}>
          <div className={styles.search}>
            <Search size={14} />
            <input
              placeholder="Buscar por código, mensaje, componente, consulta, uid…"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
            {texto && <X size={14} className={styles.clear} onClick={() => setTexto('')} />}
          </div>
          <div className={styles.select}>
            <Filter size={13} />
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">Todas las categorías</option>
              {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="SIN_CLASIFICAR">Sin clasificar</option>
            </select>
          </div>
          <div
            className={styles.select}
            title="Qué eventos guarda el backend en el visor. Se registran los de este nivel y todos los superiores. Aplica en caliente."
          >
            <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Registrar eventos:</span>
            <select value={nivelMin} onChange={(e) => cambiarNivelMin(e.target.value as Nivel)}>
              {NIVEL_MIN_OPCIONES.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
            </select>
          </div>
          {trazaId && (
            <div className={styles.traza}>
              <Activity size={13} /> Traza: <code>{trazaId}</code>
              <X size={13} className={styles.clear} onClick={() => setTrazaId('')} />
            </div>
          )}
        </div>

        {/* Tabla */}
        <div className={styles.tableWrap}>
          {error ? (
            <div className={styles.empty}>
              <AlertTriangle size={16} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 6 }} />
              No se pudieron cargar los eventos: {error.message}
              {/permission|insufficient/i.test(error.message) && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                  Requiere rol Administrador o Supervisor.
                </div>
              )}
            </div>
          ) : loading && !eventosRaw ? (
            <div className={styles.empty}>Cargando eventos…</div>
          ) : (eventosRaw?.length ?? 0) === 0 ? (
            <div className={styles.empty}>
              Aún no hay eventos registrados.
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                El backend guarda los eventos del nivel seleccionado en «Registrar eventos» y superiores.
                Si está en «Sólo críticos», sólo verás errores graves.
              </div>
            </div>
          ) : filtrados.length === 0 ? (
            <div className={styles.empty}>Sin eventos que coincidan con los filtros.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Hora</th><th>Nivel</th><th>Categoría</th><th>Código</th>
                  <th>Componente</th><th>Consulta</th><th>Autor</th><th>Mensaje</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((e) => (
                  <tr
                    key={e.id}
                    className={`${styles.rowClickable} ${e.nivel === 'ERROR' || e.nivel === 'FATAL' ? styles.rowErr : ''}`}
                    onClick={() => setSelected(e)}
                    title="Ver detalle del evento"
                  >
                    <td className={styles.mono}>{fmtHora(e.timestamp)}</td>
                    <td><span className={`${styles.badge} ${nivelCls(e.nivel)}`}>{e.nivel}</span></td>
                    <td>{e.categoria?.trim() || 'Sin clasificar'}</td>
                    <td className={styles.mono}>{e.codigo}</td>
                    <td>{e.componente ?? '—'}</td>
                    <td>
                      {e.consultaId ? (
                        <button
                          className={styles.link}
                          onClick={(ev) => { ev.stopPropagation(); setTrazaId(e.consultaId!); }}
                          title="Filtrar por la traza de esta consulta"
                        >
                          {e.consultaId.slice(0, 10)}…
                        </button>
                      ) : '—'}
                    </td>
                    <td title={e.actor?.rol ? `Rol: ${e.actor.rol}` : undefined}>{nombreActor(e)}</td>
                    <td className={styles.msg}>
                      {e.mensaje ?? ''}
                      {e.error?.mensaje && <span className={styles.errMsg}> · {e.error.mensaje}</span>}
                    </td>
                    <td className={styles.detailCell}>
                      <span className={styles.detailHint} aria-hidden="true">Detalle ›</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className={styles.note}>
          Mostrando los últimos {eventos.length} eventos (tiempo casi real). Filtros aplicados en memoria.
        </p>
      </div>

      {/* ─── Detalle del evento ─── */}
      {selected && (
        <div className={styles.detailOverlay} onClick={() => setSelected(null)}>
          <div
            className={styles.detailModal}
            role="dialog"
            aria-modal="true"
            aria-label="Detalle del evento"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.detailHead}>
              <div className={styles.detailHeadL}>
                <span className={`${styles.badge} ${nivelCls(selected.nivel)}`}>{selected.nivel}</span>
                <span className={styles.detailCode}>{(selected.categoria?.trim() || 'Sin clasificar')} · {selected.codigo}</span>
              </div>
              <div className={styles.detailHeadR}>
                <button className={styles.detailCopy} onClick={copiarEvento} type="button">
                  {copiado ? <Check size={14} /> : <Copy size={14} />} {copiado ? 'Copiado' : 'Copiar JSON'}
                </button>
                <button className={styles.detailClose} onClick={() => setSelected(null)} type="button" aria-label="Cerrar">
                  <X size={18} />
                </button>
              </div>
            </div>

            {selected.mensaje && <p className={styles.detailMsg}>{selected.mensaje}</p>}

            <div className={styles.detailGrid}>
              {([
                ['Hora', fmtHora(selected.timestamp), false],
                ['Origen', selected.origen ?? null, false],
                ['Componente', selected.componente ?? null, false],
                ['Resultado', selected.resultado ?? null, false],
                ['Duración', selected.duracionMs != null ? `${selected.duracionMs} ms` : null, false],
                ['Entorno', selected.entorno ?? null, false],
                ['Consulta', selected.consultaId ?? null, true],
                ['Sala', selected.salaId ?? null, true],
                ['Traza', selected.traceId ?? null, true],
                ['Autor', nombreActor(selected), false],
                ['Actor UID', selected.actor?.uid ?? null, true],
                ['Rol', selected.actor?.rol ?? null, false],
              ] as [string, string | null, boolean][])
                .filter(([, v]) => v != null && v !== '')
                .map(([label, value, mono]) => (
                  <div key={label} className={styles.detailField}>
                    <span className={styles.detailLabel}>{label}</span>
                    <span className={mono ? styles.mono : undefined}>{value}</span>
                  </div>
                ))}
            </div>

            {selected.error && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailSecTitle}>
                  <AlertTriangle size={14} aria-hidden="true" /> Error
                  {selected.error.tipo && <code className={styles.detailErrType}>{selected.error.tipo}</code>}
                </h4>
                {selected.error.mensaje && <pre className={styles.detailPre}>{selected.error.mensaje}</pre>}
                {selected.error.stack && (
                  <>
                    <div className={styles.detailSubh}>Stack trace</div>
                    <pre className={styles.detailPre}>{selected.error.stack}</pre>
                  </>
                )}
              </div>
            )}

            {selected.detalle && Object.keys(selected.detalle).length > 0 && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailSecTitle}>Detalle</h4>
                <pre className={styles.detailPre}>{JSON.stringify(selected.detalle, null, 2)}</pre>
              </div>
            )}

            {selected.consultaId && (
              <div className={styles.detailActions}>
                <button
                  className={styles.detailTraceBtn}
                  type="button"
                  onClick={() => { setTrazaId(selected.consultaId!); setSelected(null); }}
                >
                  <Activity size={14} aria-hidden="true" /> Ver toda la traza de esta consulta
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Purgar eventos viejos ─── */}
      {showPurga && (
        <div className={styles.detailOverlay} onClick={() => !purgando && setShowPurga(false)}>
          <div className={styles.purgaModal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className={styles.detailHead}>
              <div className={styles.detailHeadL}>
                <Trash2 size={16} aria-hidden="true" />
                <span className={styles.detailCode}>Purgar eventos viejos</span>
              </div>
              <button className={styles.detailClose} onClick={() => setShowPurga(false)} type="button" aria-label="Cerrar" disabled={purgando}>
                <X size={18} />
              </button>
            </div>

            <p className={styles.detailMsg}>
              Borra eventos de observabilidad antiguos para mantener el visor liviano.
              Por seguridad, <strong>nunca se borran eventos de menos de 7 días</strong> y,
              por defecto, se <strong>conservan los de error y críticos</strong>.
            </p>

            <label className={styles.purgaField}>
              <span className={styles.detailLabel}>Borrar eventos con más de</span>
              <select value={purgaDias} onChange={(e) => setPurgaDias(Number(e.target.value))} disabled={purgando}>
                <option value={90}>90 días</option>
                <option value={60}>60 días</option>
                <option value={30}>30 días</option>
                <option value={15}>15 días</option>
                <option value={7}>7 días (mínimo permitido)</option>
              </select>
            </label>

            <label className={styles.purgaCheck}>
              <input type="checkbox" checked={purgaErrores} onChange={(e) => setPurgaErrores(e.target.checked)} disabled={purgando} />
              <span>Incluir también errores y críticos (por defecto se conservan)</span>
            </label>

            {purgaMsg && <div className={styles.purgaMsg}>{purgaMsg}</div>}

            <div className={styles.detailActions} style={{ display: 'flex', gap: 10 }}>
              <button className={styles.detailCopy} onClick={() => setShowPurga(false)} type="button" disabled={purgando}>Cancelar</button>
              <button className={styles.purgaBtn} onClick={purgar} type="button" disabled={purgando}>
                <Trash2 size={14} /> {purgando ? 'Purgando…' : `Purgar eventos > ${purgaDias} días`}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
